// Shared by the guest-apply consent copy and the footer legal link so the two
// can never diverge.
const PRIVACY_POLICY_URL = 'https://synmatch.ai/privacy-policy';
const POSITIVE_OBSERVATIONS_LABEL = 'Positive observations';
const AREAS_FOR_IMPROVEMENT_LABEL = 'Areas for improvement';

// One line for every disclosed PDF artifact, so CV and Report cannot drift.
// The Client company-profile page and the Request Interview dialog read the same
// resource, so its wait and failure copy cannot diverge between them. The same
// holds for the field the two surfaces both edit: one label, one length rule.
const COMPANY_PROFILE_LOADING = 'Loading company profile...';
const CANDIDATE_INTRODUCTION_LABEL = 'About the company';

const CANDIDATE_INTRODUCTION_TOO_LONG =
  'Keep this to 1,000 characters or fewer.';

// Every counted Client field reads this, so a running total cannot be phrased two
// ways. Both numbers are grouped, or a full field reads `1000 / 1,000` with the
// same value in two formats. The max is always passed in rather than written
// into the string, so it cannot drift from the rule that enforces it.
const CHARACTER_COUNTER = (current: number, max: number) =>
  `${current.toLocaleString('en-US')} / ${max.toLocaleString('en-US')}`;

// The Pushed to Client stage filter and the card badge name the same fact, so
// the queue is labelled in one place.
const CANDIDATE_FEEDBACK_REQUIRED_LABEL = 'Candidate feedback required';

// The first invitation and its resend both stop on the same completed-feedback
// state, so the two surfaces explain it in one sentence.
const CLIENT_HANDOFF_READ_ONLY_COPY =
  'Synmatch AI already completed candidate feedback for this candidate.';

const COMPANY_PROFILE_UNAVAILABLE = {
  heading: 'Company profile unavailable',
  message:
    'We could not load the company profile. Check your connection and try again.',
};

// Viewer chrome shared by every document; titles and download labels stay document-specific.
const DOCUMENT_VIEWER_SIZE_COPY = {
  expandAction: 'Expand preview',
  restoreAction: 'Restore preview size',
};

// The label above a tile names the document; the tile only carries the verb.
const DOCUMENT_TILE_COPY = {
  attachmentAction: 'View',
};

const CV_TILE_COPY = {
  ...DOCUMENT_TILE_COPY,
  openFileAction: (fileName: string) => `View CV ${fileName}`,
  preparingAction: 'Preparing CV...',
};

const CV_VIEWER_COPY = {
  ...DOCUMENT_VIEWER_SIZE_COPY,
  previewTitle: 'Candidate CV',
  previewFrameTitle: 'CV preview',
  previewCloseLabel: 'Close CV preview',
  downloadAction: 'Download CV',
  notPreviewable: {
    heading: 'Preview not available',
    message:
      "This file type can't be previewed in the browser. Download it to review the CV.",
  },
};

export const en = {
  regionLabels: {
    WORLD: 'World',
    EU: 'EU',
    EEA: 'EEA',
    GCC: 'GCC',
    EMEA: 'EMEA',
    APAC: 'APAC',
    MENA: 'MENA',
    NORTH_AMERICA: 'North America',
    LATAM: 'LATAM',
    AFRICA: 'Africa',
  },
  generic: {
    genericError:
      'Sorry for the inconvenience, we are experiencing some issues and working to get them fixed ASAP.',
    loading: 'Loading...',
    retry: 'Retry',
    cancel: 'Cancel',
    save: 'Save',
    submit: 'Submit',
  },
  pagination: {
    rowsPerPage: 'Rows per page:',
    displayedRows: (from: number, to: number, count: number) =>
      `${from}–${to} of ${count}`,
  },
  // Shared by Admin preview and Client detail; section headings stay backend-owned.
  shareOverview: {
    headlineLabel: 'Headline',
    locationLabel: 'Location',
    emailLabel: 'Email',
    phoneLabel: 'Phone',
    requiredQuestion: 'Required',
    optionalQuestion: 'Optional',
    notAnswered: 'Not answered',
    // One card for who the candidate is and how to reach them. The three
    // backend sections behind it are a disclosure boundary, not a reading order.
    profileHeading: 'Profile',
    cv: {
      ...CV_TILE_COPY,
      rowLabel: 'CV',
      viewer: CV_VIEWER_COPY,
    },
  },
  packageEvidence: {
    navigationLabel: 'Candidate evidence groups',
    groups: {
      overview: 'Overview',
      preScreening: 'Pre-Screening',
      digitalInterview: 'Digital Interview',
    },
    sections: {
      preScreeningSummary: 'Screening summary',
      digitalInterviewSummary: 'Interview summary',
      digitalInterviewTranscript: 'Transcript',
      digitalInterviewReport: 'Report',
    },
    // Both JobMojito scores are on a 0 to 10 scale. The scale is never in the
    // contract, so it is stated here once and every score reads through it.
    scoreOutOfTen: (score: string) => `${score} / 10`,
    decisionSignals: {
      preScreening: 'Pre-screening score',
      digitalInterview: 'Digital interview score',
      accessibleLabel: (label: string, score: string) =>
        `${label} ${score} out of 10`,
    },
    fields: {
      score: 'Score',
      analysis: 'Analysis',
      education: 'Education',
      yearsOfExperience: 'Years of experience',
      switchingProfession: 'Profession switch',
      switchingProfessionValue: 'Career change flagged',
      recruiterAnalysis: 'Digital interview analysis',
      positiveObservations: POSITIVE_OBSERVATIONS_LABEL,
      areasForImprovement: AREAS_FOR_IMPROVEMENT_LABEL,
    },
    transcript: {
      question: 'Question',
      candidateAnswer: 'Candidate answer',
      noAnswer: 'No answer provided.',
      empty: 'No transcript available.',
      showMore: 'Show full transcript',
      showLess: 'Show less',
    },
    digitalInterviewReport: {
      ...DOCUMENT_TILE_COPY,
      rowLabel: 'Report',
      openFileAction: (fileName: string) => `View report ${fileName}`,
      preparingAction: 'Preparing report...',
      viewer: {
        ...DOCUMENT_VIEWER_SIZE_COPY,
        previewTitle: 'Digital Interview report',
        previewFrameTitle: 'Digital Interview report preview',
        previewCloseLabel: 'Close Digital Interview report preview',
        downloadAction: 'Download report',
        notPreviewable: {
          heading: 'Preview not available',
          message:
            "This file type can't be previewed in the browser. Download it to review the report.",
        },
      },
    },
  },
  notFoundPage: {
    heading: 'Page not found',
    message: 'This page does not exist or is no longer available.',
    actionsLabel: 'Not found actions',
    homeLink: 'Go home',
    adminHomeLink: 'Go to admin home',
  },
  brand: {
    appName: 'Synmatch AI',
    wordmarkAlt: 'Synmatch AI',
  },
  publicShell: {
    header: {
      navLabel: 'Primary',
      openRoles: 'Open roles',
      imHiring: "I'm Hiring",
    },
    footer: {
      tagline: 'AI-assisted, human-reviewed hiring.',
      privacyPolicyUrl: PRIVACY_POLICY_URL,
      privacyPolicyLabel: 'Privacy Policy',
      copyright: '© 2026 Synmatch AI',
    },
  },
  appErrorBoundary: {
    heading: 'Something went wrong',
    message:
      'An unexpected error stopped this page from displaying. Reload the page to keep going.',
    reloadAction: 'Reload page',
  },
  publicJobPage: {
    loading: 'Loading job...',
    backToRoles: 'All open roles',
    hero: {
      eyebrow: (employmentType: string, location: string) =>
        `${employmentType} · ${location}`,
    },
    summary: {
      locationLabel: 'Location',
      employmentTypeLabel: 'Employment type',
      compensationLabel: 'Compensation',
    },
    closed: {
      heading: 'Applications are currently closed',
      message: 'This job isn’t accepting new applications.',
      notSubmitted: 'Your application was not submitted',
      notSubmittedMessage:
        'This job stopped accepting applications before your submission could be completed.',
      viewOpenRoles: 'View open roles',
    },
    notAvailable: {
      heading: 'This job is no longer available',
      message:
        'The role may have been filled or closed, and is not accepting applications right now.',
    },
    unavailable: {
      heading: 'Job unavailable',
      message:
        'This job could not be loaded right now. Try again in a few minutes.',
    },
    applyCta: 'Apply for this role',
    apply: {
      heading: 'Apply for this role',
      helper:
        'Add your contact details, answer the role questions, and upload your CV.',
      aboutYouHeading: 'About you',
      fields: {
        firstNameLabel: 'First name',
        lastNameLabel: 'Last name',
        emailLabel: 'Email',
        phoneLabel: 'Phone',
        phonePlaceholder: '+971 50 123 4567',
        headlineLabel: 'Headline',
        locationLabel: 'Address',
      },
      consent: {
        requiredPrefix: 'I agree to the Synmatch AI',
        privacyLinkLabel: 'privacy policy',
        privacyPolicyUrl: PRIVACY_POLICY_URL,
        requiredSuffix:
          'and to Synmatch AI processing my application for this role.',
        contactPreferenceLabel:
          'I agree to receive application updates by SMS or WhatsApp.',
      },
      questionsHeading: 'A few questions',
      questionYesLabel: 'Yes',
      questionNoLabel: 'No',
      cvHeading: 'Your CV',
      cv: {
        uploadAction: 'Upload your CV',
        replaceAction: 'Replace CV',
        helper: 'PDF only, up to 10 MB.',
        removeAction: 'Remove CV',
      },
      validation: {
        firstNameRequired: 'Add your first name.',
        lastNameRequired: 'Add your last name.',
        emailRequired: 'Add your email address.',
        emailInvalid: 'Add a valid email address.',
        emailTooLong: 'Use an email address under 254 characters.',
        nameTooLong: 'Keep names under 120 characters.',
        optionalProfileFieldTooLong: 'Keep this under 200 characters.',
        phoneRequired: 'Add your phone number.',
        phoneInvalid:
          'Add a valid phone number — 6 to 32 characters, digits, spaces, and + - ( ) only.',
        privacyRequired: 'You need to accept the privacy policy to apply.',
        answerRequired: 'This question is required.',
        answerTooLong: (maxCharacters: number) =>
          `Keep this answer under ${maxCharacters} characters.`,
        answerNumberInvalid: 'Add a plain number — digits only, like 7 or 7.5.',
        answerDateInvalid: 'Add a valid date.',
        cvRequired: 'Attach your CV to apply.',
        cvInvalidType: 'Your CV must be a PDF file.',
        cvTooLarge: 'Your CV must be 10 MB or smaller.',
      },
      validationSummary: 'Check the highlighted fields and try again.',
      actions: {
        submit: 'Submit application',
        submitting: 'Submitting application...',
      },
      failure: {
        generic:
          'Your application could not be submitted. Check your connection and try again.',
        validation:
          'Some details could not be accepted. Review your answers and try again.',
      },
      alreadyApplied: {
        heading: 'We already have your application',
        message:
          'You have already applied for this role with this email address. Your application is with our team. There is nothing more you need to do.',
      },
      applicationsClosed: {
        heading: 'Applications are closed',
        message:
          'This role is not accepting new applications right now. The job page stays available while the role is open.',
      },
      confirmation: {
        heading: 'Application received',
        message: (firstName: string, jobTitle: string, email: string) =>
          `Thanks, ${firstName}. Your application for ${jobTitle} is in. We will review your profile for this role and email you at ${email} with next steps.`,
        roleLabel: 'Role',
        applicantLabel: 'Applicant',
        cvLabel: 'CV',
        questionsAnsweredLabel: 'Questions answered',
        questionsAnswered: (answeredCount: number, totalCount: number) =>
          `${answeredCount} of ${totalCount}`,
        statusLabel: 'Status',
        // PRD §13 candidate-facing vocabulary, verbatim — never invent
        // synonyms.
        statusLabels: {
          Applied: 'Applied',
          'Under Review': 'Under Review',
          'Interview Stage': 'Interview Stage',
          Offer: 'Offer',
          Hired: 'Hired',
          'Not Selected': 'Not Selected',
          Withdrawn: 'Withdrawn',
        },
      },
    },
  },
  publicJobsList: {
    eyebrow: 'Open roles on Synmatch AI',
    heading: 'Open roles',
    // count is the full single string for aria; countSuffix omits the number
    // because the visible line renders it as a separate bolded JSX node.
    count: (openRoleCount: number) =>
      `${openRoleCount} open ${openRoleCount === 1 ? 'role' : 'roles'}`,
    countSuffix: (openRoleCount: number) =>
      ` open ${openRoleCount === 1 ? 'role' : 'roles'}`,
    loadingLabel: 'Loading open roles',
    filters: {
      searchLabel: 'Search roles',
      searchPlaceholder: 'e.g. Backend engineer',
      locationLabel: 'Location',
      locationPlaceholder: 'Anywhere',
      typeLabel: 'Employment type',
      typeAll: 'All types',
    },
    empty: {
      heading: 'No open roles right now',
      message:
        'We post roles as our partner companies open them — check back soon; every new role lands here first.',
    },
    noMatches: 'No roles match your filters. Try clearing your search.',
    error: {
      heading: 'We couldn’t load the roles',
      message:
        'Something went wrong on our side, not yours. Give it another moment and try again.',
      retry: 'Retry',
    },
  },
  publicLanding: {
    loadingLabel: 'Loading',
    hero: {
      eyebrow: 'Open roles on Synmatch AI',
      // Rendered as `<em>{headlineLead}</em>{headlineRest}` in a single h1, so
      // the lead and rest concatenate to the full accessible headline.
      headlineLead: 'Always know',
      headlineRest: ' where you stand.',
      cta: 'Browse open roles',
      subcopy:
        'Synmatch AI is a structured, transparent platform to connect you to remote hiring companies. Anonymized listings, a real human review, and constructive feedback on your interview performance — no black box, no ghosting.',
    },
    livePreview: {
      eyebrow: 'Live now',
      title: 'Recently opened roles',
      viewAll: 'View all roles',
      empty: 'No open roles right now — check back soon.',
      errorHeading: 'We couldn’t load roles right now',
      errorMessage: 'Give it another moment and try again.',
    },
    howItWorksSection: {
      eyebrow: 'How it works',
      title: 'Six steps. No surprises.',
    },
    forCompanies: {
      eyebrow: 'For hiring companies',
      body: 'Synmatch AI partners with a select group of companies hiring remotely.',
      cta: "I'm Hiring",
    },
    howItWorks: {
      // Step numbers (01–06) are continuous across the two phases and computed
      // by the component, not stored. Copy is locked — do not reword or add steps.
      phases: [
        {
          tag: 'With Synmatch AI',
          steps: [
            {
              name: 'Apply',
              body: 'Submit your profile and CV to apply for a role.',
            },
            {
              name: 'Under review',
              body: 'A Synmatch AI specialist reviews your profile and screens your application.',
            },
            {
              name: 'AI interview',
              body: 'Complete a short AI interview to help assess your skills and experience.',
            },
            {
              name: 'Feedback',
              body: 'You always hear back with instant constructive feedback on your interview performance.',
            },
          ],
        },
        {
          tag: 'With the hiring company',
          steps: [
            {
              name: 'Company selection',
              body: 'You are forwarded to the hiring company for their selection process.',
            },
            {
              name: 'Hired',
              body: 'Your next remote role with transparent compensation awaits.',
            },
          ],
        },
      ],
    },
  },
  auth: {
    callback: {
      loading: 'Completing sign-in...',
      error: {
        heading: 'Sign-in failed',
        message: 'We could not complete sign-in. Try signing in again.',
      },
    },
    error: {
      heading: 'Admin sign-in failed',
      message: 'We could not complete admin sign-in. Try signing in again.',
    },
    missingSession: {
      heading: 'Admin session unavailable',
      message:
        'We could not read a valid admin session. Log out and choose your Synmatch admin account again.',
    },
    redirectingToSignIn: 'Redirecting to sign in...',
    signIn: {
      kicker: 'Synmatch AI Operations',
      heading: 'Admin sign in',
      intro:
        "Internal access for the Synmatch AI team. You'll be redirected to sign in to continue.",
      action: 'Sign in',
      secureHint: 'Secure sign-in',
      altLead: 'Not staff? ',
      altBrowseRoles: 'Browse open roles',
      altConnector: ' or ',
      altClientSignIn: 'client sign in',
      altTrail: '.',
    },
    signOut: 'Log out',
    signOutAndSwitchAccount: 'Log out and switch account',
  },
  adminPage: {
    loading: 'Checking admin access...',
    accessDenied: {
      heading: 'Admin access denied',
      message:
        'This account is signed in, but it is not allowed to open Synmatch Admin. Log out and choose another account.',
    },
    unavailable: {
      heading: 'Admin access unavailable',
      message:
        'We could not reach the admin API to verify access. Try again after the API is reachable.',
    },
    verified: {
      heading: 'Synmatch Admin',
      message: 'Admin access verified.',
      subjectLabel: 'Subject',
      authoritiesLabel: 'Authorities',
      actionsLabel: 'Admin actions',
      clientsLink: 'Clients',
      jobIntakesLink: 'Job Intakes',
      createJobIntakeLink: 'Create job intake',
    },
  },
  clientPage: {
    loading: 'Checking client workspace access...',
    signIn: {
      action: 'Sign in',
      kicker: 'Client workspace',
      heading: 'Welcome back',
      message:
        'Sign in to your client workspace. Synmatch AI access is by invitation only.',
      secureHint: 'Secure sign-in',
      altLead: 'Looking for a role instead? ',
      altBrowseRoles: 'Browse open roles',
    },
    signInFailed: {
      heading: 'Client sign-in failed',
      message: 'We could not complete client sign-in. Try signing in again.',
    },
    missingSession: {
      heading: 'Client session unavailable',
      message:
        'We could not read a valid client session. Log out and choose your client workspace account again.',
    },
    accessDenied: {
      heading: 'Client access unavailable',
      message:
        'This account is signed in, but it is not connected to this workspace. Log out and choose another account.',
    },
    unavailable: {
      heading: 'Client workspace unavailable',
      message:
        'We could not reach the client workspace API to verify access. Try again after the API is reachable.',
    },
    terms: {
      heading: 'Review terms before entering your workspace',
      railBrand: 'Synmatch AI',
      railMessage:
        'Client workspace access starts after Synmatch records your current terms acceptance.',
      description:
        'Synmatch AI shares candidate and job data through this workspace. Please review the workspace terms before continuing.',
      checkboxPrefix: 'I have read and agree to the',
      documentAction: 'Terms & Conditions',
      acceptAction: 'Accept and continue',
      secondaryAction: 'Log out',
      submittingAction: 'Accepting...',
      failure: {
        heading: 'Terms acceptance failed',
        denied:
          'This account cannot accept workspace terms. Log out and use another account.',
        unavailable:
          'We could not accept terms right now. Check the connection and try again.',
      },
    },
    jobs: {
      heading: 'Your jobs',
      loading: 'Loading active jobs...',
      loadMore: 'Load more',
      count: (total: number) =>
        `${total} active ${total === 1 ? 'role' : 'roles'} with Synmatch AI`,
      loadedCount: (loaded: number, total: number) =>
        `Showing ${loaded} of ${total} roles`,
      empty: {
        heading: 'No active jobs yet',
        message: 'There are no active jobs to review right now.',
      },
      failure: {
        heading: 'Active jobs unavailable',
        unavailable:
          'We could not load active jobs. Try again after the API is reachable.',
        accessNotReady:
          'Jobs access is not ready for this account. Try again after terms are refreshed.',
      },
    },
    companyProfile: {
      heading: 'Company profile',
      description:
        'Manage your company details and how your company is introduced to candidates.',
      companyNameLabel: 'Company name',
      candidateIntroductionLabel: CANDIDATE_INTRODUCTION_LABEL,
      candidateIntroductionHelper:
        'Give candidates a brief introduction to your company and what it’s like to work there.',
      websiteLabel: 'Company website',
      websiteHelper:
        'Use a secure website address, such as https://example.com.',
      locationLabel: 'Company location',
      saveAction: 'Save changes',
      savingAction: 'Saving changes...',
      saved: 'Changes saved.',
      validation: {
        candidateIntroductionTooLong: CANDIDATE_INTRODUCTION_TOO_LONG,
        websiteInvalid: 'Enter a valid website address that supports HTTPS.',
        websiteTooLong: 'Keep this to 2,048 characters or fewer.',
        locationTooLong: 'Keep this to 160 characters or fewer.',
      },
      failure: {
        heading: 'Could not save your company profile',
        validation:
          'Some details could not be accepted. Review your changes and try again.',
        unavailable: 'Check your connection and try again.',
      },
      conflict: {
        heading: 'This company profile has changed',
        message:
          'Another Client User saved changes. Reload the current profile before trying again.',
        reloadAction: 'Reload current profile',
      },
      loading: COMPANY_PROFILE_LOADING,
      unavailable: COMPANY_PROFILE_UNAVAILABLE,
    },
    shortlistedCandidates: {
      heading: 'Shortlisted candidates',
      backToJobs: 'Back to jobs',
      viewJobPost: 'View job post',
      viewJobPostLabel: (jobTitle: string) =>
        `Open the ${jobTitle} job post in a new tab`,
      loading: 'Loading shortlisted candidates...',
      unavailable: {
        heading: 'Shortlisted candidates unavailable',
        message: 'We could not load shortlisted candidates. Try again.',
      },
      boardLabel: 'Shortlisted candidate stages',
      empty: 'Candidates shortlisted by Synmatch AI will appear here.',
      emptyStage: 'No candidates',
      stageLabel: (stageLabel: string) => `${stageLabel} candidates`,
      countLabel: (stageLabel: string, count: number) =>
        `${stageLabel} candidate count: ${count}`,
      shortlistedOn: (formattedDate: string) =>
        `Shortlisted by Synmatch AI on ${formattedDate}`,
      stagePickerLabel: 'Stage',
      loadMore: 'Load more',
      loadMoreLabel: (stageLabel: string) =>
        `Load more ${stageLabel} candidates`,
      loadingMore: (stageLabel: string) =>
        `Loading more ${stageLabel} candidates...`,
      loadMoreFailed: {
        heading: (stageLabel: string) =>
          `Could not load more ${stageLabel} candidates`,
        message: (stageLabel: string) =>
          `The loaded ${stageLabel} candidates are still shown. Try again to load the next page.`,
        retryLabel: (stageLabel: string) =>
          `Retry loading more ${stageLabel} candidates`,
      },
      // Read by the board card chip and the candidate detail status section, so
      // one delivery state can never be named two ways.
      invitationDelivery: {
        heading: 'Interview invitation',
        status: {
          queued: 'Invitation queued',
          sent: 'Invitation sent',
          failed: 'Invitation failed',
        },
        statusAge: (status: string, age: string) => `${status} · ${age}`,
        retryAction: 'Retry sending',
        retryingAction: 'Retrying…',
        retryQueued: 'Invitation queued again.',
        retryFailed: 'We could not resend this invitation. Try again.',
        conflict: {
          notFailed:
            'This invitation is no longer failed. We loaded the latest delivery status.',
          retryUnavailable:
            'Resending is no longer available for this invitation. We loaded the latest details.',
          handoffReadOnly: CLIENT_HANDOFF_READ_ONLY_COPY,
          generic:
            'We could not resend this invitation. We loaded the latest details.',
        },
      },
      rejection: {
        handledChip: 'Feedback handled',
        feedbackRequired: {
          heading: 'Synmatch AI is reviewing the feedback.',
          message:
            'The candidate has not been contacted about this rejection yet.',
        },
        feedbackHandled: {
          heading: 'Feedback handled by Synmatch AI.',
          message: 'The candidate has been given feedback on this rejection.',
        },
        readOnly: 'This candidate is now read-only.',
      },
      detail: {
        fallbackTitle: 'Candidate details',
        closeLabel: 'Close candidate details',
        closeAction: 'Close',
        loading: 'Loading candidate details...',
        queue: {
          previous: 'Previous candidate',
          next: 'Next candidate',
          position: (position: number, total: number) =>
            `${position} of ${total}`,
          positionLabel: (position: number, total: number) =>
            `Candidate ${position} of ${total}`,
          loadingNext: 'Loading more candidates...',
          loadMoreFailed: {
            heading: 'Could not load the next candidate',
            message:
              'The next page of candidates could not load. Retry, or return to the board.',
            retryAction: 'Retry loading next candidate',
            returnAction: 'Return to board',
          },
        },
        move: {
          actionsLabel: 'Candidate stage actions',
          currentStage: (stageLabel: string) => `Current stage: ${stageLabel}`,
          trigger: 'Move to…',
          requestInterviewDestination: 'Request interview',
          pending: 'Moving candidate…',
          destinationsLoading: 'Stage options are still loading.',
          destinationsUnavailable:
            'Stage options are unavailable right now. Refresh and try again.',
          success: (stageName: string) => `Candidate moved to ${stageName}.`,
          unchanged: (stageName: string) =>
            `This candidate is already in ${stageName}.`,
          failed: 'We could not move this candidate. Try again.',
          invalid: 'We could not move this candidate. Refresh and try again.',
          conflict: {
            generic:
              'We could not complete this move. We loaded the latest stage.',
            handoffReadOnly: CLIENT_HANDOFF_READ_ONLY_COPY,
            stageChanged:
              'Another Client User moved this candidate. We loaded the latest stage.',
            unavailable: 'This move is not available for this candidate.',
          },
        },
        requestInterview: {
          title: 'Request interview',
          description:
            "Confirm what goes into this candidate's first interview invitation.",
          // Both notes render as their own field's helper text, so the field
          // label already supplies the subject each sentence used to repeat.
          companyProfileNote: 'Changes update the shared Company Profile.',
          schedulingLinkNote:
            'Changes become the Job default for future invitations.',
          candidateIntroductionLabel: CANDIDATE_INTRODUCTION_LABEL,
          counter: CHARACTER_COUNTER,
          schedulingLinkLabel: 'Interview scheduling link (optional)',
          submitAction: 'Send invitation',
          submittingAction: 'Sending invitation…',
          reloadAction: 'Reload current values',
          reloadingAction: 'Reloading…',
          loading: COMPANY_PROFILE_LOADING,
          unavailable: COMPANY_PROFILE_UNAVAILABLE,
          queued: 'Invitation queued.',
          failed: 'We could not send this invitation. Try again.',
          invalid:
            'We could not send this invitation. Check these values and try again.',
          conflict: {
            companyProfileChanged:
              'About the company changed in another session.',
            schedulingLinkChanged:
              'The scheduling link changed in another session.',
            invitationExists:
              'This candidate already has an interview invitation. We loaded the latest details.',
            handoffReadOnly: CLIENT_HANDOFF_READ_ONLY_COPY,
            unavailable:
              'Synmatch AI has to correct required candidate or Job information before this invitation can be sent.',
            generic:
              'We could not complete this invitation. We loaded the latest details.',
          },
          validation: {
            candidateIntroductionRequired:
              'Enter what candidates should know about your company.',
            candidateIntroductionTooLong: CANDIDATE_INTRODUCTION_TOO_LONG,
            schedulingLinkInvalid:
              'Enter a scheduling link that starts with https:// and has no sign-in details.',
            schedulingLinkRewritten:
              'This is not exactly the link we would send. Check the address, including capital letters, and enter it exactly as the candidate should receive it.',
            schedulingLinkTooLong: 'Keep this to 2,048 characters or fewer.',
          },
        },
        decline: {
          trigger: 'Decline candidate',
          title: 'Decline candidate',
          description:
            'The candidate will not be contacted about this rejection yet. Your reason and note are internal and will be reviewed by Synmatch AI before candidate feedback is sent.',
          reasonLabel: 'Reason',
          reasonPlaceholder: 'Select reason',
          reasons: {
            skillsOrExperienceMismatch: 'Skills or experience mismatch',
            interviewOutcome: 'Interview outcome',
            compensationMismatch: 'Compensation mismatch',
            availabilityOrTiming: 'Availability or timing',
            locationOrWorkArrangement: 'Location or work arrangement',
            roleOrBusinessNeedsChanged: 'Role or business needs changed',
            other: 'Other',
          },
          noteLabel: 'Internal note',
          noteHint: 'Optional internal context for Synmatch AI.',
          counter: CHARACTER_COUNTER,
          noteRequiredHint: 'Required when the reason is Other.',
          submitAction: 'Mark as not selected',
          submittingAction: 'Marking as not selected…',
          // Names who, because the queue advances the moment this fires and the
          // Undo beside it acts on a candidate no longer on screen. What
          // Synmatch AI does next is the rejection alert's job on the detail,
          // not a toast's.
          success: (candidateName: string) =>
            `${candidateName} marked as not selected.`,
          undo: {
            action: 'Undo',
            pendingAction: 'Undoing…',
            undone: (stageName: string) =>
              `Rejection undone. Candidate returned to ${stageName}.`,
            failed: 'We could not undo this rejection. Try again.',
            conflict: {
              candidateChanged:
                'Rejection could not be undone because the candidate changed.',
              handoffReadOnly:
                'Feedback was already handled, so this rejection cannot be undone.',
              generic:
                'We could not complete this undo. We loaded the latest details.',
            },
          },
          failed:
            'We could not mark this candidate as not selected. Try again.',
          invalid:
            'We could not mark this candidate as not selected. Check these values and try again.',
          conflict: {
            handoffReadOnly: CLIENT_HANDOFF_READ_ONLY_COPY,
            generic:
              'We could not complete this rejection. We loaded the latest details.',
          },
          validation: {
            reasonRequired: 'Select the reason for declining this candidate.',
            noteRequired: 'Add an internal note when the reason is Other.',
            noteTooLong: 'Keep this to 2,000 characters or fewer.',
          },
        },
        notFound: 'This candidate is no longer available.',
        unavailable: {
          heading: 'Candidate details unavailable',
          message: 'We could not load this candidate. Try again.',
        },
        cvUnavailable: {
          heading: 'CV is currently unavailable.',
          message: '',
        },
        digitalInterviewReportUnavailable: {
          heading: 'Report currently unavailable.',
          message: '',
        },
        digitalInterviewReportRetry: 'Try again',
      },
    },
  },
  clientShell: {
    account: {
      companyProfile: 'Company profile',
    },
    brand: {
      productName: 'Synmatch AI',
    },
    companyProfileLinkLabel: (companyName: string, location?: string) =>
      location
        ? `Open ${companyName} company profile, ${location}`
        : `Open ${companyName} company profile`,
    navigationLabel: 'Client workspace',
    navigation: {
      jobs: 'Jobs',
    },
  },
  adminShell: {
    brand: {
      productName: 'Synmatch AI',
      sectionName: 'Admin',
    },
    navigationLabel: 'Admin modules',
    navigation: {
      clients: 'Clients',
      jobIntakes: 'Job Intakes',
      jobs: 'Jobs',
      applications: 'Applications',
    },
  },
  adminApplicationsPage: {
    heading: 'Applications',
    description: 'Review submitted applications across the live admin stages.',
    loading: 'Loading applications...',
    unauthenticated: {
      heading: 'Admin session required',
      message: 'Use a configured admin session before viewing applications.',
    },
    unavailable: {
      heading: 'Applications unavailable',
      message:
        'We could not load applications. Try again after the API is reachable.',
    },
    board: {
      searchLabel: 'Search by name or email',
      clearSearch: 'Clear search',
      searchTooLong: 'Search must be 512 characters or fewer.',
      searching: 'Searching...',
      searchingAnnouncement: 'Searching applications',
      searchUnavailable: 'Results unavailable',
      totalApplications: (count: number) =>
        `${count.toLocaleString()} ${
          count === 1 ? 'application' : 'applications'
        }`,
      showingApplications: (count: number, total: number) =>
        `Showing ${count.toLocaleString()} of ${total.toLocaleString()}`,
      showingApplicationsAnnouncement: (count: number, total: number) =>
        `Showing ${count.toLocaleString()} of ${total.toLocaleString()} ${
          total === 1 ? 'application' : 'applications'
        }`,
      noMatches: 'No applications match this search.',
      searchCoverage:
        'Search covers candidate name and email across every stage in this board, including Declined and Withdrawn.',
      searchFeedbackFilterHint:
        'Candidate feedback required still filters Pushed to Client.',
      mobileStageLabel: 'Stage',
      stageColumnLabel: (stageLabel: string) => `${stageLabel} applications`,
      // The badge is a plain span, so its accessible name has to carry the
      // number itself.
      countBadgeLabel: (count: number) => `Stage application count: ${count}`,
      // The stage name is already the column header directly above this empty
      // state, so it is deliberately not repeated here.
      emptyStage: () => 'No applications yet',
      emptyStageForJob: () => 'No applications yet',
      loadMore: 'Load more',
      // Accessible name keeps the stage context the visible "Load more" label
      // drops, so the control reads unambiguously outside its column region.
      loadMoreLabel: (stageLabel: string) =>
        `Load more ${stageLabel} applications`,
      loadMoreFailed: {
        heading: 'Could not load more applications',
        message: (stageLabel: string) =>
          `The loaded ${stageLabel} applications are still shown. Try again to load the next page.`,
      },
      candidateFeedbackFilter: {
        label: CANDIDATE_FEEDBACK_REQUIRED_LABEL,
        // The icon-only trigger carries no visible text, so the stage it
        // narrows is named here for the tooltip and the accessible name alike.
        triggerLabel: 'Filter Pushed to Client',
        menuTitle: 'Filter this stage',
        countBadgeLabel: (count: number) =>
          `Candidates requiring feedback count: ${count}`,
        emptyStage: 'No candidates currently require feedback.',
      },
    },
    card: {
      openApplication: (candidateName: string, appliedAge: string) =>
        `Open ${candidateName}'s application, applied ${appliedAge}`,
      // The link's aria-label replaces the card content, so a handed-off card
      // states its client stage and entry age here or announces neither.
      openApplicationInClientStage: (
        candidateName: string,
        appliedAge: string,
        stageLabel: string,
        stageAge: string
      ) =>
        `Open ${candidateName}'s application, applied ${appliedAge}, in client stage ${stageLabel} entered ${stageAge}`,
      // A required-feedback card trades its client-stage chip for the badge, so
      // the spoken name states the same one fact the card shows.
      openApplicationRequiringFeedback: (
        candidateName: string,
        appliedAge: string
      ) =>
        `Open ${candidateName}'s application, applied ${appliedAge}, candidate feedback required`,
      candidateFeedbackRequiredChip: CANDIDATE_FEEDBACK_REQUIRED_LABEL,
    },
    stages: {
      inbox: 'Inbox',
      inReview: 'Pre-Screening',
      aiInterview: 'Invited for Digital Interview',
      interviewReview: 'Digital Interview Submitted',
      pushedToClient: 'Pushed to Client',
      declined: 'Declined',
      withdrawn: 'Withdrawn',
    },
    detail: {
      fallbackHeading: 'Application detail',
      closeLabel: 'Close',
      backLink: 'Back to applications',
      loading: 'Loading application...',
      queue: {
        previous: 'Previous applicant',
        next: 'Next applicant',
        position: (position: number, total: number) =>
          `${position} of ${total}`,
        loadingNext: 'Loading more applicants...',
        loadMoreFailed: {
          heading: 'Could not load more applicants',
          message:
            'The next page of applicants could not load. Retry, or close to return to the board.',
        },
        concurrentSkip: (count: number) =>
          `${count} ${
            count === 1 ? 'application was' : 'applications were'
          } already moved by another admin, so ${
            count === 1 ? 'it was' : 'they were'
          } skipped.`,
      },
      notProvided: 'Not provided',
      noAnswers: 'No answers were submitted.',
      tabs: {
        label: 'Application detail sections',
        overview: 'Overview',
        aiInterview: 'AI Interview',
        history: 'History',
      },
      comingSoon: {
        tag: 'Soon',
        tooltip: 'Coming soon',
      },
      layout: {
        overviewWorkspace: 'Application overview workspace',
      },
      comms: {
        railLabel: 'Communications',
        messages: 'Messages',
        internalNotes: 'Internal notes',
        comingSoon: {
          heading: 'Coming soon',
          body: 'Candidate messages and internal notes will live here once communications ship.',
        },
      },
      unauthenticated: {
        heading: 'Admin session required',
        message: 'Use a configured admin session before viewing applications.',
      },
      notFound: {
        heading: 'Application not found',
        message: 'This application does not exist or is no longer available.',
      },
      unavailable: {
        heading: 'Application unavailable',
        message:
          'We could not load this application. Try again after the API is reachable.',
      },
      sections: {
        profile: 'Candidate profile',
        actions: 'Application actions',
        answers: 'Question answers',
        decline: 'Decline feedback',
      },
      fields: {
        email: 'Email',
        phone: 'Phone',
        headline: 'Headline',
        location: 'Location',
        salaryExpectation: 'Salary expectation',
        cv: 'CV',
        internalDeclineReason: 'Internal reason',
        candidateFeedback: 'Candidate feedback',
      },
      answers: {
        required: 'Required',
        optional: 'Optional',
        notAnswered: 'Not answered',
        count: (answeredCount: number, questionCount: number) =>
          `${answeredCount} of ${questionCount} answered`,
      },
      cv: {
        ...CV_TILE_COPY,
        viewAction: 'View CV',
        fallbackFilename: 'candidate-cv.pdf',
        ...CV_VIEWER_COPY,
        unavailable: {
          heading: 'CV unavailable',
          message:
            'We could not open the stored CV. Try again after the file is available.',
        },
        blocked: {
          heading: 'CV unavailable',
          message: 'The stored CV is not currently available for admin review.',
        },
      },
      actions: {
        startReview: 'Move to Pre-screening',
        startingReview: 'Moving to Pre-screening...',
        invite: 'Invite to Digital Interview',
        inviting: 'Sending invite...',
        pushToClient: 'Push to client',
        sendCandidateFeedback: 'Send candidate feedback',
        decline: 'Decline',
        cvScanPendingNote: 'CV scan pending',
        cvBlockedNote: 'CV blocked by file safety checks',
        declinedNote: 'Declined',
        movedToPreScreening: {
          heading: 'Moved to Pre-Screening',
        },
        invited: {
          heading: 'Invited to Digital Interview',
        },
        declined: {
          heading: 'Application declined',
        },
        conflict: {
          heading: 'Application changed',
          message: 'This application changed. Review the latest state.',
        },
        pushSuccessToast: {
          betweenNames: 'was pushed to',
          end: '.',
        },
        candidateFeedbackSavedToast:
          'Candidate feedback saved. Application moved to Declined.',
        // One notice for both ways the task can go stale: the backend rejecting
        // the completion, and a detail refetch that no longer requires feedback.
        candidateFeedbackStale: {
          heading:
            'The Client changed this candidate, so feedback is no longer required.',
        },
        alreadyPushedToClient: {
          heading: 'Already pushed',
          message: 'This candidate was already pushed to the client.',
        },
        failed: {
          heading: 'Action failed',
          message:
            'We could not update this application. Try again after the API is reachable.',
        },
      },
      clientRejection: {
        heading: 'Client rejection',
        reasonLabel: 'Reason',
        noteLabel: 'Internal note',
        actorLabel: 'Submitted by',
        submittedLabel: 'Submitted',
        noNote: 'No note provided',
        internalOnlyTag: 'Internal only',
        showMore: 'Show more',
        showLess: 'Show less',
      },
      declineDialog: {
        title: 'Decline application',
        body: 'Choose the internal reason and review the final candidate-facing feedback before declining.',
        reasonLabel: 'Internal reason',
        reasonPlaceholder: 'Choose a reason',
        feedbackLabel: 'Candidate feedback',
        closeLabel: 'Close decline dialog',
        feedbackCounter: (current: number, max: number) =>
          `${current} / ${max}`,
        submit: 'Decline application',
        submitting: 'Declining application...',
        reasons: {
          overallFit: 'Overall fit',
          experienceMismatch: 'Experience mismatch',
          timezoneRequirements: 'Timezone requirements',
          roleFilled: 'Role filled',
        },
        suggestions: {
          overallFit: `Hi {{firstName}},

Thank you for your interest in the position {{jobTitle}} through Synmatch AI.

After carefully reviewing your application, we have decided not to move forward with your candidacy for this position. This decision reflects the specific requirements of the role and the highly competitive applicant pool, rather than the value of your experience and accomplishments.

We sincerely appreciate your interest in joining our team and encourage you to keep an eye on future opportunities that may be a strong match for your background.

We wish you every success in your job search and future career endeavors.

Warm regards,
Synmatch AI`,
          experienceMismatch:
            'Hi {{firstName}}, thank you for taking the time to apply for the {{jobTitle}} role. After reviewing your application, we will not be moving forward because your experience is not the right match for this role. We appreciate your interest and wish you the best with your search.',
          timezoneRequirements:
            "Hi {{firstName}}, thank you for applying for the {{jobTitle}} role. We will not be moving forward because the role's timezone requirements do not align closely enough with your current availability. We appreciate your interest and wish you the best with your search.",
          roleFilled:
            'Hi {{firstName}}, thank you for applying for the {{jobTitle}} role. We will not be moving forward because the role has now been filled. We appreciate your interest and wish you the best with your search.',
        },
        validation: {
          reasonRequired: 'Choose an internal reason.',
          feedbackRequired: 'Add candidate feedback before declining.',
          feedbackTooLong:
            'Candidate feedback must be 4000 characters or less.',
          feedbackHasTokens:
            'Resolve candidate feedback placeholders before declining.',
        },
        failed: {
          heading: 'Decline failed',
          message:
            'We could not decline this application. Try again after the API is reachable.',
        },
        invalid: {
          heading: 'Decline needs review',
          message:
            'Check the decline reason and candidate feedback before trying again.',
        },
      },
      candidateFeedbackDialog: {
        title: 'Send candidate feedback',
        description:
          'This will move the application to Declined and save the candidate-facing feedback for email delivery.',
        internalReasonNote:
          "The Client's reason is internal and will not be sent automatically.",
        feedbackLabel: 'Candidate-facing feedback',
        submit: 'Send feedback and decline',
        submitting: 'Sending feedback…',
        // Field-level, not a dialog banner: a backend rejection of the body is
        // the same kind of answer the client-side rules give.
        feedbackRejected:
          'Check the candidate-facing feedback before sending again.',
        failed: {
          heading: 'Sending feedback failed',
          message:
            'We could not send this candidate feedback. Try again after the API is reachable.',
        },
      },
      sharingDialog: {
        selectionTitle: 'Choose what the client can see',
        previewTitle: 'Review client view',
        closeLabel: 'Close sharing dialog',
        requiredMarker: 'Required',
        // The visible label carries the Required marker beside it; the control's
        // own accessible name has to say it too.
        requiredSectionLabel: (sectionLabel: string) =>
          `${sectionLabel}, required`,
        reviewAction: 'Review client view',
        pushAction: 'Push to client',
        preparingPreview: 'Preparing preview...',
        previewHelper: (clientName: string) =>
          `Preview of the information ${clientName} will see.`,
        targetClient: (clientName: string) => `Client ${clientName}`,
        targetJob: (jobTitle: string) => `Job ${jobTitle}`,
        back: 'Back',
        submit: 'Push candidate to client',
        submitting: 'Pushing...',
        sectionsUnavailable:
          'Some selections are no longer available. Review your choices before continuing.',
        staleReview: {
          heading: 'Client view updated',
          message:
            "This candidate's information changed. Review the updated client view before pushing.",
        },
        pipelineUnavailable: {
          heading: "Can't push right now",
          message:
            "This job's client pipeline is unavailable. Try again later.",
        },
        failed: {
          heading: 'Something went wrong',
          message: 'Try again.',
        },
      },
    },
    aiInterviewOverview: {
      heading: 'AI interview',
      scoreLabel: 'AI interview score',
      accessibleScoreLabel: (score: string) => `AI interview score ${score}`,
    },
    declineEmailDelivery: {
      status: {
        notQueued: 'No decline email sent',
        sending: 'Sending',
        sent: 'Sent',
        failed: 'Email failed',
        missingEmail: 'Missing email',
      },
      timestampLabel: 'Email status updated',
      resend: 'Resend decline email',
      resending: 'Starting send...',
      feedback: {
        success: {
          heading: 'Sending again',
          message: "We'll try sending the decline email again.",
        },
        conflict: {
          heading: 'Resend no longer available',
          message:
            'The email status changed, so this action is no longer available. We refreshed the application.',
        },
        failure: {
          heading: 'Could not resend email',
          message: 'We could not start another send attempt. Please try again.',
        },
      },
    },
    aiInterviewInvite: {
      blockedReason: {
        inviteExists: 'This candidate already has a Digital Interview invite.',
        invitesDisabled: 'AI interviews are turned off for this job.',
        notConfigured:
          'AI interviews are not set up yet. Contact your administrator.',
        invalidStage:
          'This candidate is not in a stage that can be invited to a Digital Interview.',
        missingJobLink: 'This job is not linked to an AI interview yet.',
        missingCandidateEmail:
          'This candidate has no email address to send the invite to.',
        invalidCandidateEmail:
          "This candidate's email address is not valid for sending the invite.",
        fallback:
          'This candidate cannot be invited to a Digital Interview yet.',
      },
      heading: 'Digital Interview invite',
      linkStatusLabel: 'Invite link status',
      emailStatusLabel: 'Email status',
      linkStatus: {
        pending: 'Preparing invite',
        failed: 'Invite failed',
        ready: 'Invite ready',
      },
      emailStatus: {
        notQueued: 'No invite email sent',
        queued: 'Sending',
        sent: 'Sent',
        missingEmail: 'Missing email',
        failed: 'Email failed',
      },
      // Board-card exception chips: only the two failure states surface here,
      // as short operational copy distinct from the detail status-row labels.
      cardChip: {
        linkFailed: 'Invite link failed',
        emailFailed: 'Email failed',
      },
      timestampLabel: 'Email status updated',
      actions: {
        retry: 'Retry invite',
        retrying: 'Retrying invite...',
        resend: 'Resend email',
        resending: 'Starting send...',
        refresh: 'Refresh',
      },
      feedback: {
        retrySuccess: {
          heading: 'Retrying invite',
          message: "We'll try preparing the Digital Interview invite again.",
        },
        resendSuccess: {
          heading: 'Sending again',
          message: "We'll try sending the invite email again.",
        },
        conflict: {
          heading: 'Action no longer available',
          message:
            'This application changed, so this action is no longer available. Review the latest status.',
        },
        failure: {
          heading: 'Could not complete action',
          message: 'We could not complete that action. Please try again.',
        },
      },
    },
    aiInterviewResult: {
      heading: 'AI interview result',
      // Backend-owned result status enum -> recruiter-facing label. Vendor
      // name, raw enum values, and error detail never reach the UI.
      status: {
        notReceived: 'Not received',
        importPending: 'Processing',
        importFailed: 'Import failed',
        imported: 'Imported',
      },
      // The three non-imported states share one quiet StateAlert shape. Copy is
      // verbatim product wording; never surface raw backend/vendor errors.
      states: {
        notReceived: {
          heading: 'Not received',
          body: 'No AI interview result has been received for this application yet.',
        },
        importPending: {
          heading: 'Processing',
          body: 'The AI interview result was received and is being imported.',
        },
        importFailed: {
          heading: 'Import failed',
          body: 'We could not import this AI interview result.',
          retryableBody:
            'We could not import this AI interview result. Retry the import.',
        },
      },
      sections: {
        summary: 'Recruiter summary',
        scoreBreakdown: 'Score breakdown',
      },
      fields: {
        resultStatus: 'Result status',
        score: 'Score',
        receivedAt: 'Result received',
        importedAt: 'Import completed',
        summary: 'Summary',
        whyHire: POSITIVE_OBSERVATIONS_LABEL,
        whyNotHire: AREAS_FOR_IMPROVEMENT_LABEL,
        scoreAnswer: 'Answer score',
        scoreSentiment: 'Sentiment score',
        wordsPerMinute: 'Words per minute',
        pronunciation: 'Pronunciation score',
        coverage: 'Coverage',
        completionReason: 'Completion reason',
      },
      transcript: {
        heading: 'Transcript',
        interviewerPrompt: 'Interviewer prompt',
        candidateAnswer: 'Candidate answer',
        showMore: 'Show full transcript',
        showLess: 'Show less',
      },
      jobMojitoResult: {
        openAction: 'Open in JobMojito',
      },
      // In-app AI interview report preview. Never surface the vendor artifact
      // URL, storage key, or raw backend error; failures collapse to one quiet
      // sanitized message.
      report: {
        ...DOCUMENT_TILE_COPY,
        ...DOCUMENT_VIEWER_SIZE_COPY,
        openFileAction: (fileName: string) => `View report ${fileName}`,
        preparingAction: 'Preparing report...',
        downloadAction: 'Download report',
        previewTitle: 'AI interview report',
        previewFrameTitle: 'AI interview report preview',
        previewCloseLabel: 'Close AI interview report preview',
        notPreviewable: {
          heading: 'Preview not available',
          message:
            "This file type can't be previewed in the browser. Download it to review the report.",
        },
        unavailable: {
          heading: 'Report unavailable',
          message:
            'We could not open the AI interview report. Try again after it is available.',
        },
        downloadFilename: 'ai-interview-report.pdf',
      },
      // Recovery action for a failed result import. Success needs no alert: the
      // result moves back to Processing and the refreshed detail re-renders the
      // panel. Conflict/failure copy never leaks backend/vendor detail.
      retry: {
        action: 'Retry import',
        pending: 'Retrying import...',
        feedback: {
          conflict: {
            heading: 'Retry no longer available',
            message:
              'This result changed, so retrying the import is no longer available. We refreshed the application.',
          },
          failure: {
            heading: 'Could not retry import',
            message: 'We could not retry the import. Please try again.',
          },
        },
      },
    },
    // JobMojito pre-screening result surfaced on the Overview tab. The score
    // renders to one decimal. failureReason is backend-authored admin-safe copy
    // rendered as given. Raw vendor payloads, ids, recommendations, and provider
    // errors are never rendered.
    preScreening: {
      heading: 'Pre-screening result',
      scoreLabel: 'Pre-screening score',
      accessibleScoreLabel: (score: string) => `Pre-screening score ${score}`,
      // Non-completed state chips shown beside the section heading.
      status: {
        pending: 'Screening',
        failed: 'Screening failed',
      },
      // Source line under the heading, driven by whether the form answers were
      // included in the screening input.
      source: {
        withAnswers: 'CV + answers included',
        cvOnly: 'CV only',
      },
      fields: {
        reason: 'Reason',
        education: 'Education',
        yearsOfExperience: 'Years of experience',
        switchingProfession: 'Profession switch',
        switchingProfessionValue: 'Career change flagged',
        analysis: 'Analysis',
        screenedAt: 'Screened',
      },
      // Pending and failed bodies. The failed body is only a fallback: the
      // backend-authored failureReason is preferred when present.
      states: {
        pending: {
          heading: 'Screening in progress',
          body: "The candidate's application was sent to JobMojito for screening. This updates automatically when screening completes.",
        },
        failed: {
          heading: 'Pre-screening failed',
          body: 'We could not complete pre-screening for this application.',
        },
      },
      // Recovery action for a failed screening. Success needs no alert: the item
      // returns to pending and the refreshed detail re-renders the section.
      // Conflict/failure copy never leaks backend/vendor detail.
      retry: {
        action: 'Retry screening',
        pending: 'Retrying screening...',
        feedback: {
          conflict: {
            heading: 'Retry no longer available',
            message:
              'This screening changed, so retrying is no longer available. We refreshed the application.',
          },
          failure: {
            heading: 'Could not retry screening',
            message: 'We could not retry screening. Please try again.',
          },
        },
      },
    },
    clientHandoff: {
      // The stage label arrives from the backend and is never mapped here. The
      // `Client:` prefix keeps the value in the admin's voice and off the
      // board's own Inbox column, so a copy pass must not drop it as redundant.
      cardChip: {
        label: (stageLabel: string) => `Client: ${stageLabel}`,
        // Minute-granular signal, so the title carries the exact moment rather
        // than the applied age's calendar date.
        stageTimeTitle: (stageLabel: string, exactMoment: string) =>
          `In ${stageLabel} since ${exactMoment}`,
      },
    },
  },
  adminJobIntakesPage: {
    heading: 'Job Intakes',
    description: 'Manage job intake requests.',
    createLink: 'Create job intake',
    loading: 'Loading job intakes...',
    filtersLabel: 'Job intake status filters',
    filters: {
      all: 'All',
      draft: 'Draft',
      needsClientInput: 'Needs Client Input',
      submittedForReview: 'Submitted for Review',
      needsRevision: 'Needs Revision',
      approved: 'Approved',
    },
    empty: {
      heading: 'No job intakes yet',
      message: 'No job intakes have been created yet.',
    },
    noMatches: {
      heading: 'No matching job intakes',
      message: 'No job intakes match this status filter.',
    },
    unauthenticated: {
      heading: 'Admin session required',
      message: 'Use a configured admin session before viewing job intakes.',
    },
    unavailable: {
      heading: 'Job intakes unavailable',
      message:
        'We could not load job intakes. Try again after the API is reachable.',
    },
    notProvided: 'Not provided',
    untitled: 'Untitled draft',
    tableLabel: 'Job intakes',
    // Uses the intake title as a proxy for the job's name — the intake summary
    // does not carry the linked job's title. Revisit if the API adds one.
    viewLinkedJob: (jobTitle: string, status: string) =>
      `Open the ${jobTitle} job post (${status}) in a new tab`,
    linkedJobState: (status: string) => `${status} job`,
    jobPostNotCreated: 'Not created',
    emptyValue: '—',
    rowActions: {
      view: 'View',
      edit: 'Edit',
      createJob: 'Post job',
    },
    columns: {
      intake: 'Intake',
      client: 'Client',
      status: 'Status',
      employmentType: 'Employment type',
      updatedAt: 'Updated',
      linkedJob: 'Job post',
      action: 'Action',
    },
    detail: {
      fallbackHeading: 'Job intake',
      loading: 'Loading job intake...',
      backLink: 'Back to intakes',
      overviewHeading: 'Overview',
      reviewHeading: 'Intake details',
      questionsListLabel: 'Interview questions',
      questionsShortfall: (minimum: number) =>
        `Add at least ${minimum} interview questions to approve.`,
      sections: {
        clientContact: 'Client & contact',
        roleBasics: 'Role basics',
        requirementsInterview: 'Requirements & interview',
        salaryCompensation: 'Salary & Compensation',
        locationEligibility: 'Location & eligibility',
        hiringProcess: 'Hiring process',
        workflow: 'Workflow',
      },
      approval: {
        sectionLabel: 'Approval',
        action: 'Approve intake',
        submitting: 'Approving intake...',
        helper:
          'Approval confirms this intake is ready for its job post draft.',
        approvedHelper: 'This intake is approved.',
        confirmTitle: 'Approve intake?',
        confirmBody: 'Approval marks this intake ready for its job post draft.',
        confirmAction: 'Approve',
        success: {
          heading: 'Intake approved',
          message: 'Create its job post from this page when you are ready.',
        },
        failure: {
          heading: 'Approval failed',
          message:
            'We could not approve this intake. Try again after the API is reachable.',
        },
        incomplete: {
          heading: 'Complete intake before approval',
          message:
            'Complete the highlighted fields before approving this intake.',
          editAction: 'Edit intake',
        },
      },
      discard: {
        action: 'Discard draft',
        submitting: 'Discarding draft...',
        confirmTitle: 'Discard draft?',
        confirmBody:
          'Discarding removes this draft intake from the active list. This cannot be undone.',
        confirmAction: 'Discard draft',
        failure: {
          heading: 'Discard failed',
          message:
            'We could not discard this draft. Try again after the API is reachable.',
        },
      },
      unauthenticated: {
        heading: 'Admin session required',
        message: 'Use a configured admin session before viewing this intake.',
      },
      unavailable: {
        heading: 'Job intake unavailable',
        message:
          'We could not load this job intake. Try again after the API is reachable.',
      },
      notFound: {
        heading: 'Job intake not found',
        message: 'This job intake does not exist or is no longer available.',
      },
      actions: {
        edit: 'Edit intake',
        createJobPost: 'Create job post',
        viewJobPost: 'View job post',
      },
      fields: {
        client: 'Client',
        status: 'Status',
        createdAt: 'Created',
        updatedAt: 'Updated',
        approvedAt: 'Approved at',
        linkedJobStatus: 'Linked job status',
        title: 'Job title',
        jobDescriptionLink: 'Job description link',
        employmentType: 'Employment type',
        keyResponsibilities: 'Key responsibilities',
        candidateRequirements: 'Candidate requirements',
        mustHaveRequirements: 'Must-have requirements',
        dealBreakers: 'Deal breakers',
        aiInterviewQuestions: 'Interview questions',
        compensationMinAmount: 'Minimum pay',
        compensationMaxAmount: 'Maximum pay',
        compensationPeriod: 'Pay period',
        compensationNotes: 'Compensation notes',
        workEligibilityLocations: 'Work eligibility locations',
        workLocationMode: 'Workplace type',
        workLocationScope: 'Hiring locations',
        workLocations: 'Work locations',
        workLocationNotes: 'Location notes',
        hiringTimelineProcess: 'Hiring timeline and process',
        interviewCalendarLink: 'Interview calendar link',
        additionalBenefits: 'Additional benefits',
        additionalNotes: 'Additional notes',
        pocName: 'Point of contact name',
        pocEmail: 'Point of contact email',
        pocPhone: 'Point of contact phone',
      },
    },
  },
  adminJobIntakesEditPage: {
    heading: 'Edit job intake',
    loading: 'Loading draft intake...',
    unauthenticated: {
      heading: 'Admin session required',
      message: 'Use a configured admin session before editing this intake.',
    },
    unavailable: {
      heading: 'Draft intake unavailable',
      message:
        'We could not load this draft intake. Try again after the API is reachable.',
    },
    notFound: {
      heading: 'Draft intake not found',
      message: 'This draft intake does not exist or is no longer available.',
    },
    nonDraft: {
      heading: 'Draft intake cannot be edited',
      message: 'Only Draft intakes can be edited.',
      detailLink: 'View intake',
    },
  },
  jobStatus: {
    Unpublished: 'Draft',
    Active: 'Active',
    Paused: 'Unlisted',
    'Pending Close': 'Pending Close',
    Closed: 'Closed',
  },
  adminJobsPage: {
    listing: {
      unlist: {
        action: 'Unlist job',
        title: 'Unlist this job?',
        transition: 'Listed → Unlisted',
        body: 'This job will be hidden from public listings and stop accepting new applications.',
        continuation:
          'Hiring continues for existing candidates, including interviews and the Client pipeline.',
        saving: 'Unlisting job...',
        success: 'Job unlisted.',
      },
      relist: {
        action: 'Relist job',
        title: 'Relist this job?',
        transition: 'Unlisted → Listed',
        body: 'This job will appear in public listings and accept new applications again.',
        saving: 'Relisting job...',
        success: 'Job relisted.',
      },
      explanation:
        'Hidden from public listings. New applications are paused. Hiring continues for existing candidates.',
      checking: 'Checking current listing state...',
      unverified: 'Listing state not verified',
      unknown:
        'We could not verify the current listing state. Check again before making another change.',
      reconciled:
        'The change could not be confirmed. The current server state is now shown. Review the job before making another change.',
      stale:
        'This job changed after you reviewed it. The current state is now shown. Review it and confirm again.',
      notAllowed:
        'This job cannot make that listing change. Review its current state.',
      forbidden:
        'You do not have permission to change this job. Try again with an Admin session.',
      notFound: 'This job no longer exists. Return to the jobs list.',
      failure: 'The listing change was rejected. Review the job and try again.',
      check: 'Check current state',
      review: 'Review current job',
    },
    heading: 'Jobs',
    description: 'Manage candidate-facing job posts.',
    loading: 'Loading job posts...',
    notProvided: 'Not provided',
    emptyValue: '—',
    tableLabel: 'Job posts',
    filtersLabel: 'Job post status filters',
    filters: {
      all: 'All',
      unpublished: 'Draft',
      active: 'Active',
      unlisted: 'Unlisted',
    },
    columns: {
      role: 'Role',
      client: 'Client',
      status: 'Status',
      location: 'Location',
      updatedAt: 'Updated',
    },
    rowActions: {
      edit: 'Edit',
      view: 'View',
    },
    empty: {
      heading: 'No job posts yet',
      message:
        'Job posts are created from approved job intakes. Open an approved intake and start its job post from there.',
      intakesLink: 'Go to job intakes',
    },
    noMatches: {
      heading: 'No matching job posts',
      showAll: 'Show all job posts',
    },
    unauthenticated: {
      heading: 'Admin session required',
      message: 'Use a configured admin session before viewing job posts.',
    },
    unavailable: {
      heading: 'Job posts unavailable',
      message:
        'We could not load job posts. Try again after the API is reachable.',
    },
    detail: {
      actions: {
        edit: 'Edit job post',
      },
      sections: {
        publicJobPost: 'Public job post',
        applicationQuestions: 'Application questions',
        jobState: 'Job state',
      },
      questionsListLabel: 'Application questions',
      questionRequired: 'Required',
      questionOptional: 'Optional',
      noQuestions: 'No application questions yet.',
      fields: {
        employmentType: 'Employment type',
        location: 'Location',
        compensationSummary: 'Salary Range',
        publicDescription: 'About the role',
        publicJobDescription: 'Job description',
        status: 'Status',
        client: 'Client (internal)',
        sourceIntake: 'Source intake',
        questions: 'Questions',
        published: 'Published',
        publicUrl: 'Public URL',
        createdAt: 'Created',
        updatedAt: 'Updated',
      },
      publishedNotYet: 'Not yet',
      publicUrlOnPublish: 'On publish',
      sourceIntakeFallback: 'View intake',
      sourceIntakeLinkLabel: (intakeTitle: string) =>
        `Open source intake ${intakeTitle} in a new tab`,
      publicUrlFallback: 'Public job page',
      publicUrlLinkLabel: (jobTitle: string) =>
        `Open public job page for ${jobTitle} in a new tab`,
      discard: {
        action: 'Discard draft',
        submitting: 'Discarding draft...',
        confirmTitle: 'Discard job post draft?',
        confirmBody:
          'Discarding permanently removes this job post draft. The source intake stays approved and can start a new draft. This cannot be undone.',
        confirmAction: 'Discard draft',
        failure: {
          heading: 'Discard failed',
          message:
            'We could not discard this job post draft. Try again after the API is reachable.',
        },
        conflict:
          'Only job post drafts can be discarded. Refresh to see the current status.',
        notFound: 'This job post draft no longer exists.',
      },
      publish: {
        action: 'Publish job post',
        viewPublicLink: 'View public job post',
        submitting: 'Publishing job post...',
        confirmTitle: 'Publish job post?',
        confirmBody:
          'Publishing makes this job post live at a permanent public URL. Published content cannot currently be edited. You can unlist the job to stop public listing and new applications.',
        confirmAction: 'Publish job post',
        success: 'Job post published.',
        conflict:
          'This job post is no longer a draft. Showing its current state.',
        notFound: 'This job post no longer exists.',
        failure:
          'We could not publish this job post. Try again after the API is reachable.',
        requirementsNotMet:
          "This job post no longer meets the publish requirements. The page has been refreshed to show what's missing.",
        missingFields: (fieldList: string) =>
          `Missing before publish: ${fieldList}`,
      },
    },
    workspace: {
      createHeading: 'Create job post',
      editHeading: 'Edit job post',
      backToJobs: 'Back to jobs',
      backToIntake: 'Back to intake',
      loadingIntake: 'Loading intake for this job post...',
      loadingJob: 'Loading job post draft...',
      sections: {
        publicJobPost: 'Public job post',
        applicationQuestions: 'Application questions',
        jobState: 'Job state',
      },
      questions: {
        helper:
          'Candidates answer these when applying. Optional — a post can go live without questions.',
        empty:
          'No application questions yet. Add one or copy from an existing post.',
        addQuestion: 'Add question',
        copyFromPost: 'Copy from existing post',
        cardLabel: (questionNumber: number) => `Question ${questionNumber}`,
        promptLabel: (questionNumber: number) =>
          `Question ${questionNumber} prompt`,
        promptPlaceholder:
          'e.g. How many years of backend experience do you have?',
        answerTypeLabel: 'Answer type',
        answerTypeAriaLabel: (questionNumber: number) =>
          `Question ${questionNumber} answer type`,
        requiredLabel: 'Required',
        moveUp: (questionNumber: number) =>
          `Move question ${questionNumber} up`,
        moveDown: (questionNumber: number) =>
          `Move question ${questionNumber} down`,
        remove: (questionNumber: number) => `Remove question ${questionNumber}`,
      },
      copyQuestions: {
        title: 'Copy questions from an existing post',
        postsListLabel: 'Job posts to copy from',
        loadingPosts: 'Loading job posts...',
        postsUnavailable:
          'We could not load job posts. Try again after the API is reachable.',
        empty: 'No other posts yet.',
        firstHundredHint: 'Showing the first 100 posts.',
        questionsListLabel: (postTitle: string) =>
          `Questions from ${postTitle}`,
        loadingQuestions: 'Loading questions...',
        questionsUnavailable:
          "We could not load this post's questions. Try again after the API is reachable.",
        noQuestions: 'This post has no application questions.',
        back: 'Back to posts',
        addSelected: (selectedCount: number) =>
          `Add ${selectedCount} ${
            selectedCount === 1 ? 'question' : 'questions'
          }`,
      },
      fields: {
        publicTitleLabel: 'Public job title',
        employmentTypeLabel: 'Employment type',
        employmentTypePlaceholder: 'Select employment type',
        locationLabel: 'Location',
        compensationSummaryLabel: 'Salary Range',
        compensationSummaryHelpText: 'Shown to candidates if provided.',
        publicDescriptionLabel: 'About the role — pitch',
        publicDescriptionHelpText:
          'Plain-text candidate intro. Written fresh — never copied from the internal intake.',
        publicJobDescriptionLabel: 'Job description',
        publicJobDescriptionHelpText:
          'Prefilled from the intake — remove internal criteria such as deal breakers. Supports markdown: ### headings, - bullets, **bold**. Raw HTML is removed.',
      },
      editor: {
        bold: 'Bold',
        bulletList: 'Bullet list',
        preview: 'Preview',
        edit: 'Edit',
        previewEmpty: 'Nothing to preview yet.',
      },
      validation: {
        publicTitleRequired: 'Public job title is required to save.',
        questionPromptRequired: 'Add a prompt or remove this question.',
      },
      actions: {
        saveDraft: 'Save draft',
        savingDraft: 'Saving draft...',
      },
      saveValidationFailure: 'Fix the highlighted fields before saving.',
      saveSuccess: 'Job post draft saved.',
      createFailure:
        'We could not create this job post draft. Try again after the API is reachable.',
      createConflictRedirect:
        'This intake already has a job post. Opening its draft.',
      updateFailure:
        'We could not save this job post draft. Try again after the API is reachable.',
      updateNotFound:
        'This job post draft no longer exists. It may have been discarded.',
      updateConflict: {
        heading: 'Job post is no longer editable',
        message:
          'This job post is no longer a draft. Refresh to see its current state.',
        refreshAction: 'Refresh job post',
      },
      readiness: {
        heading: 'Publish readiness',
        completeMessage: 'Ready to publish.',
        reviewAndPublishLink: 'Review & publish',
        missingSummary: (fieldList: string) =>
          `Missing before publish: ${fieldList}`,
        fields: {
          publicTitle: 'Public job title',
          publicDescription: 'About the role',
          publicJobDescription: 'Job description',
          location: 'Location',
          employmentType: 'Employment type',
        },
      },
      state: {
        statusLabel: 'Status',
        clientLabel: 'Client (internal)',
        sourceIntakeLabel: 'Source intake',
        questionsLabel: 'Questions',
        publishedLabel: 'Published',
        publicUrlLabel: 'Public URL',
        notCreatedYet: 'Not created yet',
        publishedNotYet: 'Not yet',
        publicUrlOnPublish: 'On publish',
        sourceIntakeFallback: 'View intake',
        sourceIntakeLinkLabel: (intakeTitle: string) =>
          `Open source intake ${intakeTitle} in a new tab`,
      },
      guards: {
        nonApproved: {
          heading: 'Intake is not approved yet',
          message:
            'Job posts start from approved intakes. Approve this intake before creating its job post.',
          intakeLink: 'View intake',
        },
        intakeNotFound: {
          heading: 'Job intake not found',
          message: 'This job intake does not exist or is no longer available.',
        },
        intakeUnavailable: {
          heading: 'Job intake unavailable',
          message:
            'We could not load this intake. Try again after the API is reachable.',
        },
        jobNotFound: {
          heading: 'Job post not found',
          message: 'This job post does not exist or is no longer available.',
        },
        jobUnavailable: {
          heading: 'Job post unavailable',
          message:
            'We could not load this job post. Try again after the API is reachable.',
        },
        unauthenticated: {
          heading: 'Admin session required',
          message:
            'Use a configured admin session before working on job posts.',
        },
      },
      prefill: {
        jdSections: {
          keyResponsibilities: 'Key responsibilities',
          candidateRequirements: 'Candidate requirements',
          mustHaveRequirements: 'Must-have requirements',
          dealBreakers: 'Deal breakers',
        },
        compensationRange: (minimum: string, maximum: string) =>
          `${minimum}–${maximum}`,
        compensationWithCurrency: (currency: string, amounts: string) =>
          `${currency} ${amounts}`,
        compensationFrom: (minimum: string) => `From ${minimum}`,
        compensationUpTo: (maximum: string) => `Up to ${maximum}`,
        compensationWithPeriod: (summary: string, period: string) =>
          `${summary} per ${period.toLowerCase()}`,
        locationModeWithDetail: (mode: string, detail: string) =>
          `${mode} · ${detail}`,
        worldwide: 'Worldwide',
        worldwideExcept: (locationList: string) =>
          `Worldwide except ${locationList}`,
      },
    },
    jobWorkspace: {
      backToJobs: 'Back to jobs',
      loading: 'Loading job workspace...',
      tabsLabel: 'Job workspace sections',
      tabs: {
        applications: 'Applications',
        jobPost: 'Job Post',
        aiInterview: 'AI Interview',
        preScreening: 'Pre-screening',
      },
      aiInterview: {
        indicator: {
          openTabAction: 'Open the AI Interview tab.',
          connected: 'AI Interview: Connected',
          notConnected: 'AI Interview: Not connected',
          neutral: 'AI Interview',
        },
        heading: 'AI Interview',
        loading: 'Loading AI Interview connection...',
        unavailable:
          'We could not load the AI Interview connection. Try again after the API is reachable.',
        connected: 'Connected',
        notConnected: 'No AI Interview is connected to this job post yet.',
        interviewIdLabel: 'Interview ID',
        interviewNameLabel: 'Interview name',
        interviewFlowLabel: 'Interview flow',
        interviewFlow: {
          single: 'Single step',
          multiple: 'Multiple steps',
        },
        search: {
          label: 'Search interviews',
          placeholder: 'Search JobMojito interviews by name',
          loading: 'Searching interviews...',
          noResults: 'No interviews found. Enter an ID manually below.',
          untitled: 'Untitled interview',
          errorNotice:
            'We could not load interview search. Enter the ID manually below.',
        },
        form: {
          idLabel: 'JobMojito interview ID',
          idPlaceholder: 'Paste the JobMojito interview ID',
          idRequired: 'Enter a JobMojito interview ID.',
          connectAction: 'Connect interview',
          changeAction: 'Change interview',
          submitting: 'Saving...',
        },
        unlink: {
          action: 'Remove',
          dialogTitle: 'Remove AI Interview connection?',
          dialogBody:
            'This disconnects the AI Interview from this job post. You can reconnect it later.',
          confirmAction: 'Remove connection',
          submitting: 'Removing...',
        },
        errors: {
          invalidId:
            'That JobMojito interview ID is invalid or unknown. Check the ID and try again.',
          notFound: 'This job post no longer exists. Refresh and try again.',
          conflict:
            'This connection changed in another session. Refresh and try again.',
          upstreamUnavailable:
            'JobMojito is unavailable right now. Try again in a moment.',
          generic:
            'We could not update the AI Interview connection. Try again in a moment.',
        },
      },
      preScreening: {
        heading: 'Pre-screening',
        loading: 'Loading pre-screening setup...',
        unavailable:
          'We could not load pre-screening. Try again after the API is reachable.',
        refreshFailed:
          'We could not refresh the pre-screening setup. Showing the last loaded values.',
        status: {
          notEnabled: 'Not enabled',
          enabled: 'Enabled',
          syncFailed: 'Definition sync failed',
        },
        statusLabel: 'Status',
        notEnabledBody:
          'Pre-screening is not enabled for this job post. Enable it to screen new applicants with JobMojito.',
        countryLabel: 'Position country',
        lastSyncedLabel: 'Last synced',
        neverSynced: 'Not synced yet',
        form: {
          countryLabel: 'Position country',
          countryPlaceholder: 'Select a country',
          countryHelp:
            'Country where the position is located. It does not affect scoring.',
          countryRequired: 'Select a position country.',
          expectationsLabel: 'Screening expectations',
          expectationsPlaceholder:
            'e.g. Must hold a valid work permit for the country where the position is located',
          expectationsHelp:
            'Requirements not stated in the public job description, sent to JobMojito for scoring.',
          enableAction: 'Enable pre-screening',
          saveAction: 'Save changes',
          submitting: 'Saving...',
        },
        sync: {
          help: 'Sync updates the JobMojito definition with the latest job description, questions, and screening expectations.',
          action: 'Sync definition',
          retryAction: 'Retry sync',
          submitting: 'Syncing...',
        },
        syncFailed: {
          heading: 'The pre-screening definition did not sync',
          body: 'New applicants are still screened against the last synced definition. Retry the sync to apply the latest job description, questions, and screening expectations.',
          neverSyncedBody:
            'Pre-screening is not active yet, so new applicants are not being screened. Retry the sync to create the JobMojito definition from the latest job description, questions, and screening expectations.',
        },
        disable: {
          action: 'Disable pre-screening',
          dialogTitle: 'Disable pre-screening?',
          dialogBody:
            'New applicants will no longer be pre-screened. Applicants already screened keep their results. You can enable pre-screening again later.',
          confirmAction: 'Disable pre-screening',
          submitting: 'Disabling...',
        },
        errors: {
          noJobDescription:
            'This job post needs a public job description before pre-screening can run. Add one and try again.',
          syncDisabledOrNoJobDescription:
            'Pre-screening is disabled for this job or the job post is missing its public job description. Check both and try again.',
          notFound: 'This job post no longer exists. Refresh and try again.',
          conflict:
            'Pre-screening changed in another session. Refresh and try again.',
          upstreamUnavailable:
            'JobMojito is unavailable right now. Try again in a moment.',
          generic: 'We could not update pre-screening. Try again in a moment.',
          syncGeneric:
            'We could not sync pre-screening. Try again in a moment.',
          disableGeneric:
            'We could not disable pre-screening. Try again in a moment.',
        },
      },
      notFound: {
        heading: 'Job post not found',
        message: 'This job post does not exist or is no longer available.',
      },
      unavailable: {
        heading: 'Job post unavailable',
        message:
          'We could not load this job post. Try again after the API is reachable.',
      },
      unauthenticated: {
        heading: 'Admin session required',
        message: 'Use a configured admin session before viewing this job post.',
      },
    },
  },
  adminClientsPage: {
    conflict: {
      companyName: (name: string) => `A client named ${name} already exists.`,
      email: 'A client user already exists for that email.',
      noClientProfile: 'Use a different name or check with engineering.',
      openClient: 'Open client',
    },
    heading: 'Clients',
    description: 'Manage client companies and their client users.',
    loading: 'Loading clients...',
    tableLabel: 'Clients',
    createPage: {
      heading: 'Create client',
      description:
        'Create the company profile now. Add client users when the workspace is ready.',
    },
    detailPage: {
      heading: 'Client',
      loading: 'Loading client...',
      notFound: {
        heading: 'Client not found',
        message: 'This client does not exist or has been removed.',
      },
      unavailable: {
        heading: 'Client unavailable',
        message:
          'We could not load this client. Try again after the API is reachable.',
      },
      unauthenticated: {
        heading: 'Admin session required',
        message:
          'Use a configured admin session before viewing client details.',
      },
      backLink: 'Back to clients',
      profileHeading: 'Profile',
      fields: {
        industry: 'Industry',
        website: 'Website',
        createdAt: 'Created',
        teamMembers: 'Client users',
      },
      notProvided: '—',
    },
    inviteStatus: {
      pending: 'Pending',
      sent: 'Sent',
      failed: 'Failed',
      notSent: 'Not sent',
    },
    userTable: {
      tableLabel: 'Client users',
      columns: {
        teamMember: 'Client user',
        access: 'Access',
        inviteStatus: 'Invite status',
        inviteSent: 'Invite sent',
        actions: 'Actions',
      },
      notSentDate: '—',
      access: {
        active: 'Active',
        deactivated: 'Deactivated',
      },
      resend: 'Resend',
      noAction: 'No action available',
    },
    teamAccess: {
      heading: 'Client users',
      emptyHeading: 'No client users yet',
      emptyMessage: 'Invite the first client user to give this client access.',
    },
    detailInviteForm: {
      heading: 'Invite client user',
      submit: 'Send invite',
      submitting: 'Sending invite...',
      successMessages: {
        accepted: 'Invite accepted. Check delivery status below.',
        failed: 'Invite accepted, but delivery failed. Use Resend below.',
        pending: 'Invite queued for delivery.',
        sent: 'Invite sent successfully.',
      },
    },
    stalePending: {
      message: 'Invite delivery is taking longer than usual.',
      refreshAction: 'Refresh',
    },
    empty: {
      heading: 'No clients yet',
      message: 'No clients have been created yet.',
    },
    unauthenticated: {
      heading: 'Admin session required',
      message: 'Use a configured admin session before viewing clients.',
    },
    unavailable: {
      heading: 'Clients unavailable',
      message:
        'We could not load clients. Try again after the API is reachable.',
    },
    createAction: 'Create client',
    team: {
      none: 'No client users yet',
      noInvites: 'No invites sent',
      lastInvite: (date: string) => `Last invite ${date}`,
      count: (count: number) => `${count} ${count === 1 ? 'user' : 'users'}`,
    },
    actions: {
      manage: 'Manage',
    },
    createForm: {
      heading: 'Client profile',
      description:
        'Add the basic company details for intake and workspace setup.',
      nameLabel: 'Company name',
      industryLabel: 'Industry (optional)',
      websiteLabel: 'Website (optional)',
      submit: 'Create client and send invite',
      submitting: 'Creating client and sending invite...',
      submitCreateOnly: 'Create client',
      submittingCreateOnly: 'Creating client...',
      cancel: 'Cancel',
      nameRequired: 'Company name is required.',
      websiteInvalid: 'Enter a valid website URL.',
      failure:
        'We could not create this client. Check the details and try again.',
      validationFailure: 'Check the highlighted fields and try again.',
      configurationFailure:
        'Client setup is not configured correctly. Ask the engineering team to review the configuration.',
      upstreamFailure:
        'Client setup service is unavailable right now. Try again after the service recovers.',
      inviteNowLabel: 'Invite a client user now',
      inviteNowDescription:
        'Send the first setup email now. You can follow its status on the client page.',
      inviteLaterDescription:
        'Leave this off if you want to create a client profile only and add users later.',
      inviteSectionHeading: 'First client user',
      createOnlyFailure:
        'We could not create this client. Check the details and try again.',
      createOnlyValidationFailure:
        'Check the highlighted fields and try again.',
      createOnlyConfigurationFailure:
        'Client setup is not configured correctly. Ask the engineering team to review the configuration.',
      createOnlyUpstreamFailure:
        'Client setup service is unavailable right now. Try again after the service recovers.',
    },
    inviteForm: {
      emailLabel: 'Email',
      firstNameLabel: 'First name',
      lastNameLabel: 'Last name',
      emailRequired: 'Email is required.',
      emailInvalid: 'Enter a valid email address.',
      firstNameRequired: 'First name is required.',
      lastNameRequired: 'Last name is required.',
      inviteFailure:
        'We could not send the setup invite. Check the details and try again.',
      inviteValidationFailure: 'Check the invite fields and try again.',
      inviteNotFound:
        'The client could not be found. Refresh the page and try again.',
      inviteConfigurationFailure:
        'The invite could not be queued right now. Ask the engineering team to review the configuration.',
      inviteUpstreamFailure:
        'The invite could not be queued right now. Try again after the service recovers.',
    },
    resendInvite: {
      failure: 'We could not resend the setup email. Try again.',
      notFound:
        'We could not find this client user. Refresh the page and try again.',
      notInvitable: 'This client user cannot receive a setup email right now.',
      configurationFailure:
        'Invite email sending is not configured correctly. Ask the engineering team to review the configuration.',
      upstreamFailure:
        'The setup email service is unavailable right now. Try again after the service recovers.',
    },
    columns: {
      client: 'Client',
      createdAt: 'Created',
      teamAccess: 'Client users',
      action: 'Action',
    },
  },
  adminJobIntakesCreatePage: {
    heading: 'Create job intake',
    loadingClients: 'Loading clients for job intake...',
    emptyClients: {
      heading: 'Create a client first',
      message:
        'A job intake needs an existing client. Create a client before starting this intake.',
      clientsLink: 'Go to clients',
    },
    unauthenticated: {
      heading: 'Admin session required',
      message: 'Use a configured admin session before creating job intakes.',
    },
    unavailable: {
      heading: 'Clients unavailable',
      message:
        'We could not load clients for this intake. Try again after the API is reachable.',
    },
    wizard: {
      untitledDraft: 'Untitled intake',
      steps: {
        clientContact: 'Client & contact',
        roleBasics: 'Role & location',
        requirementsInterview: 'Interview & requirements',
        salaryCompensation: 'Salary & Compensation',
        hiringProcess: 'Hiring process',
      },
      sections: {
        role: 'Role',
        location: 'Location',
        pointOfContact: 'Point of contact',
        roleProfile: 'Role profile',
      },
      actions: {
        back: 'Previous',
        next: 'Continue',
        saveDraft: 'Save draft',
        savingDraft: 'Saving draft...',
        saveAndApprove: 'Save & approve',
        approving: 'Approving...',
        addQuestion: 'Add question',
        removeQuestion: (questionNumber: number) =>
          `Remove question ${questionNumber}`,
        addWorkLocation: 'Add work location',
        addWorkEligibilityLocation: 'Add eligibility location',
        removeLocation: 'Remove',
      },
      fields: {
        clientLabel: 'Client',
        clientPlaceholder: 'Select a client',
        clientHelpText: "Client can't be changed after the first draft save.",
        clientLockedHelpText: 'Client is locked after the first draft save.',
        titleLabel: 'Job title',
        employmentTypeLabel: 'Employment type',
        employmentTypePlaceholder: 'Select employment type',
        employmentTypeOptions: [
          'Full-time',
          'Part-time',
          'Contract',
          'Freelance',
        ],
        jobDescriptionLinkLabel: 'Job description link',
        jobDescriptionLinkPlaceholder: 'https://...',
        keyResponsibilitiesLabel: 'Key responsibilities',
        keyResponsibilitiesHelpText:
          'What this person will own day to day — one point per line.',
        candidateRequirementsLabel: 'Candidate requirements',
        candidateRequirementsHelpText:
          'Skills and experience the ideal candidate brings.',
        mustHaveRequirementsLabel: 'Must-have requirements',
        mustHaveRequirementsHelpText:
          'Non-negotiables — candidates without these are screened out.',
        dealBreakersLabel: 'Deal breakers',
        dealBreakersHelpText:
          'Conditions that disqualify a candidate outright.',
        aiInterviewQuestionsLabel: 'Interview questions',
        interviewQuestionLabel: (questionNumber: number) =>
          `Question ${questionNumber}`,
        interviewQuestionPlaceholder:
          'e.g. Walk us through a recent project you owned end to end.',
        aiInterviewQuestionsHelpText:
          'These questions power the screening interviews. Add at least 5 before approval.',
        aiInterviewQuestionsProgress: (
          addedCount: number,
          requiredCount: number
        ) => `${addedCount} of ${requiredCount} added`,
        compensationMinAmountLabel: 'Minimum pay',
        compensationMinAmountPlaceholder: 'e.g. 65000',
        compensationMaxAmountLabel: 'Maximum pay',
        compensationMaxAmountPlaceholder: 'e.g. 85000',
        compensationCurrencyLabel: 'Currency',
        compensationCurrencyPlaceholder: 'Select currency',
        compensationPeriodLabel: 'Pay period',
        compensationPeriodPlaceholder: 'Select period',
        compensationNotesLabel: 'Compensation notes',
        compensationNotesHelpText:
          'Bonus, equity, commission, or anything unusual about the range.',
        workLocationModeLabel: 'Workplace type',
        workLocationModePlaceholder: 'Select workplace type',
        workLocationScopeLabel: 'Hiring locations',
        workLocationScopePlaceholder: 'Select hiring locations',
        workLocationsLabel: 'Hiring countries and regions',
        workLocationsIncludedLabel: 'Included countries and regions',
        workLocationsExcludedLabel: 'Excluded countries and regions',
        workLocationsHelpText:
          'Add any location preferences or context in the location notes.',
        workLocationsSearchPlaceholder: 'Search countries or regions',
        workEligibilityLocationsLabel: 'Work eligibility locations',
        workEligibilityLocationsHelpText:
          'Countries where candidates must already have the right to work.',
        locationScopeTypeLabel: (rowNumber: number) =>
          `Location ${rowNumber} type`,
        locationScopeTypePlaceholder: 'Select type',
        locationScopeCountry: 'Country',
        locationScopeRegion: 'Region',
        locationScopeCodeLabel: (rowNumber: number) =>
          `Location ${rowNumber} code`,
        locationScopeCodePlaceholder: 'Select code',
        workLocationNotesLabel: 'Location notes',
        workLocationNotesPlaceholder:
          'e.g. countries with low employer contributions, or high English proficiency',
        hiringTimelineProcessLabel: 'Hiring timeline and process',
        hiringTimelineProcessHelpText:
          'Stages, interviewers, and how quickly you want to move.',
        interviewCalendarLinkLabel: 'Interview calendar link',
        interviewCalendarLinkPlaceholder: 'https://...',
        additionalBenefitsLabel: 'Additional benefits',
        additionalBenefitsHelpText:
          'Perks beyond pay — visa support, learning budget, equipment.',
        additionalNotesLabel: 'Additional notes',
        additionalNotesHelpText: 'Anything else that helps us run this search.',
        pocNameLabel: 'Name',
        pocNamePlaceholder: 'Full name',
        pocEmailLabel: 'Email',
        pocEmailPlaceholder: 'name@company.com',
        pocPhoneLabel: 'Phone',
        pocPhonePlaceholder: '+49 151 23456789',
      },
      validation: {
        clientRequired: 'Client is required.',
        locationScopeTypeRequired: 'Choose a location type.',
        locationScopeCodeRequired: 'Choose a location code.',
        workLocationModeRequiredForLocations:
          'Choose a workplace type for the entered countries or regions.',
        workLocationScopeRequiredForLocations:
          'Choose hiring locations for the entered countries or regions.',
        workLocationsRequiredForMode:
          'Add at least one country or region for the selected workplace type.',
        workLocationDraftSaveIncomplete:
          'Add at least one country or region for this workplace type, or clear the workplace type to save without it.',
        remoteAnywhereWorkLocationsInvalid:
          'Remove work locations for Remote Anywhere.',
        titleRequired: 'Job title is required for approval.',
        employmentTypeRequired: 'Employment type is required for approval.',
        keyResponsibilitiesRequired:
          'Key responsibilities are required for approval.',
        candidateRequirementsRequired:
          'Candidate requirements are required for approval.',
        mustHaveRequirementsRequired:
          'Must-have requirements are required for approval.',
        dealBreakersRequired: 'Deal breakers are required for approval.',
        aiInterviewQuestionsRequired:
          'Add at least 5 interview questions for approval.',
        compensationAmountInvalid: 'Enter a number greater than or equal to 0.',
        compensationMaxInvalid:
          'Enter a maximum compensation greater than or equal to the minimum.',
        pocEmailRequired: 'Point of contact email is required for approval.',
        pocEmailInvalid: 'Enter a valid point of contact email.',
        pocPhoneRequired: 'Point of contact phone is required for approval.',
        pocPhoneInvalid: 'Enter a valid point of contact phone number.',
        urlInvalid: 'Enter a valid http or https URL.',
      },
      saveFailure:
        'We could not save this draft intake. Check the details and try again.',
      saveRequiresClient: 'Select a client before saving the draft.',
      saveSuccess: {
        heading: 'Draft saved',
        message: (title: string, clientName: string) =>
          `Saved draft intake "${title}" for ${clientName}.`,
        // One-word verb in the toast; the accessible name stays self-describing.
        detailLink: 'View',
        detailLinkLabel: 'View draft intake',
      },
      approveFailure: 'Draft saved, but approval failed. Try again.',
      approveValidationFailure: 'Fix the highlighted fields before approving.',
      approveSuccess: {
        message: (title: string, clientName: string) =>
          `Approved intake "${title}" for ${clientName}.`,
      },
    },
  },
} as const;

export const GENERIC_ERROR_MESSAGE = en.generic.genericError;
